import React, { useState, useEffect, memo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  View,
  SecondaryText,
  SmallText,
  Header,
  Box,
  Row,
  HoverButton,
  InlineLink,
} from 'modules/styled';
import { timeLeftTick, abbreviateNumber as toFixed } from 'utils/numbers';
import { VOTE_COST_RATIO, MAX_BET } from '@r3l/common';
import CoinStat from 'modules/stats/coinStat.component';
import { bet } from 'modules/post/invest.actions';
import { goToUrl } from 'modules/navigation/navigation.actions';
import Tooltip from 'modules/tooltip/tooltip.component';
import { exchangeLink, tokenEnabled } from 'modules/wallet/price.context';
import { useCommunity } from 'modules/community/community.selectors';
import BetStats from './betstats';
import CircleButton from './circlebutton';
import SmallCoinStat from './smallcoinstat';
import { Captcha } from 'utils/recaptcha/mobile';

export function BetContainer({ ...props }) {
  const user = useSelector((state) => state.auth.user) || {};
  const post = useSelector(
    (state) => state.posts.posts[state.navigation.modalData.postId]
  );
  if (!user || !post) return null;
  return <Bet user={user} post={post} {...props} />;
}

Bet.propTypes = {
  user: PropTypes.object,
  post: PropTypes.object,
  close: PropTypes.func,
};

function Bet({ user, post, close }) {
  const dispatch = useDispatch();
  const community = useCommunity();
  const [recaptcha, setCaptcha] = useState();
  const reCaptchaRef = useRef();
  const earning = useSelector((state) =>
    !community
      ? null
      : state.earnings.pending
          .map((e) => state.earnings.entities[e])
          .find((ee) => ee.post === post._id && ee.communityId === community?._id)
  );
  const [processingBet, setProcessingBet] = useState(false);

  const title = earning ? 'Increase Your Bet' : 'Bet on the Relevance of this Post';
  const existingStake = earning?.stakedTokens || 0;
  const totalBalance = user.balance + user.tokenBalance;
  const maxBet = Math.min(MAX_BET - existingStake, totalBalance - user.lockedTokens);
  const defaultAmount = Math.max(maxBet * VOTE_COST_RATIO, 0);
  const [amount, setAmount] = useState(defaultAmount);

  // const time = getTimestamp(post.data.payoutTime).toLowerCase();
  const [time, setTimer] = useState(timeLeftTick(post?.data?.payoutTime));

  useEffect(() => {
    if (!post.data) return () => null;
    const id = setInterval(() => setTimer(timeLeftTick(post?.data?.payoutTime)), 1000);
    return () => clearInterval(id);
  }, [post?.data?.payoutTime, time]);

  if (!user || !post || !post.data) return null;

  const plusAmount = () =>
    setAmount((a) => {
      const largeStep = (maxBet - defaultAmount) / 5;
      const smallStep = defaultAmount / 5;
      const err = defaultAmount / 100;
      return a + smallStep <= defaultAmount + err
        ? a + smallStep
        : Math.min(a + largeStep, maxBet);
    });

  const minusAmount = () =>
    setAmount((a) => {
      const largeStep = (maxBet - defaultAmount) / 5;
      const smallStep = defaultAmount / 5;
      const err = defaultAmount / 100;
      if (a - smallStep <= 0 + err) return a;
      return a - largeStep >= defaultAmount - err
        ? a - largeStep
        : Math.max(0, a - smallStep);
    });

  const placeBet = async () => {
    try {
      setProcessingBet(true);
      await dispatch(bet({ postId: post._id, stakedTokens: amount, recaptcha }));
      setProcessingBet(false);
      close();
    } catch (err) {
      setProcessingBet(false);
    }
  };

  const exchageUrl = exchangeLink();

  const power = (100 * amount) / maxBet;

  const tooltipData = {
    text: 'Posts that get upvoted by lots of users with high Reputation get payouts.\n\nBet more coins and bet early in order to win the biggest portion of the payout.',
  };

  return (
    <View>
      <Header sx={{ mr: 2 }}>
        {title} <Tooltip inline={1} name={'betInfo'} data={tooltipData} info />
      </Header>

      <Row sx={{ alignItems: 'baseline' }}>
        <SmallText sx={{ mt: 1, mr: 1 }}>Time until payout: {time}</SmallText>
      </Row>

      <Row sx={{ mt: 4, justifyContent: 'space-between', alignItems: 'center' }}>
        <CircleButton onPress={minusAmount}>–</CircleButton>
        <View sx={{ pr: 1 }}>
          <CoinStat
            align={'center'}
            fs={4.5}
            lh={4.5}
            size={5}
            spaceBetween={1}
            c={'black'}
            amount={amount}
          />
        </View>
        <CircleButton onPress={plusAmount}>+</CircleButton>
      </Row>
      <View sx={{ mt: 2 }}>
        <Row sx={{ height: 4.001 }}>
          <Box sx={{ width: `${power}%`, bg: 'blue' }} />
          <Box sx={{ width: `${100 - power}%`, bg: 'lightBorder' }} />
        </Row>
        <Row sx={{ justifyContent: 'space-between' }}>
          <SecondaryText sx={{ mt: 4.001 }}>
            Available Coins:{' '}
            <SmallCoinStat amount={totalBalance - user.lockedTokens - amount} />
          </SecondaryText>
          {tokenEnabled() && (
            <InlineLink
              hu
              inline
              to={exchageUrl}
              nativeAction={() => dispatch(goToUrl(exchageUrl))}
              external
              target="_blank"
            >
              <SecondaryText sx={{ color: 'blue', lineHeight: 2 }}>
                Get more coins
              </SecondaryText>
            </InlineLink>
          )}
        </Row>
      </View>

      <View sx={{ mt: 3 }}>
        <BetStats maxBet={maxBet} post={post} amount={amount} earning={earning} />
      </View>

      <HoverButton sx={{ mt: 3 }} onPress={placeBet} disabled={processingBet || !amount}>
        Bet {toFixed(amount)} Coins
      </HoverButton>

      <SmallText sx={{ mt: 2 }}>
        *All coins will be returned to your wallet 3 days after you place your bet.
      </SmallText>
      <Captcha reCaptchaRef={reCaptchaRef} action={'bet'} setToken={setCaptcha} />
    </View>
  );
}

export default memo(BetContainer);
